// components/MyPerformance.jsx
import React, { useState, useEffect } from "react";
import {
  Target,
  Calendar,
  TrendingUp,
  Award,
  RefreshCw,
  CheckCircle2,
  Flame,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Line,
  ComposedChart,
} from "recharts";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const DAY_LABELS = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};
const pct = (a, t) => (t > 0 ? Math.round((a / t) * 100) : 0);
const perfColor = (p) =>
  p >= 100 ? "text-green-400" : p >= 75 ? "text-yellow-400" : "text-red-400";

const MyPerformance = () => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [granularity, setGranularity] = useState("daily");

  const pageBg = isDarkMode
    ? "bg-[#0A0B0F] text-white"
    : "bg-gray-50 text-gray-900";
  const cardBg = isDarkMode
    ? "bg-[#0C0E12] border-gray-700"
    : "bg-white border-gray-200";
  const mutedText = isDarkMode ? "text-[#94979C]" : "text-gray-500";

  const load = async () => {
    try {
      setLoading(true);
      const res = await apiService.getMyPerformance({ weeks: 4, granularity });
      setData(res.data);
    } catch (e) {
      console.error("Error loading performance:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [granularity]);

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${pageBg}`}
      >
        <RefreshCw className="animate-spin text-[#427BCA]" size={32} />
      </div>
    );
  }

  if (!data?.hasTargets) {
    return (
      <div className={`min-h-screen p-6 ${pageBg}`}>
        <div
          className={`max-w-2xl mx-auto rounded-xl border p-12 text-center ${cardBg}`}
        >
          <Target
            size={48}
            className="mx-auto mb-4 text-[#427BCA] opacity-50"
          />
          <h2 className="text-xl font-semibold mb-2">No targets set</h2>
          <p className={mutedText}>
            Ask your manager to configure performance metrics for your team.
          </p>
        </div>
      </div>
    );
  }

  const primary = data.primaryMetric;
  const today = new Date().toISOString().split("T")[0];
  const todayStat = primary?.dailyStats?.find((d) => d.date === today) || {
    target: 0,
    actual: 0,
    percentageAchieved: 0,
  };

  return (
    <div className={`min-h-screen p-6 font-inter ${pageBg}`}>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Target className="text-[#427BCA]" size={32} />
              My Performance
            </h1>
            <p className={mutedText}>
              {data.teams?.map((t) => t.teamName).join(" · ")}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Granularity selector */}
            <div className="flex gap-1 p-1 rounded-lg border border-gray-700/40">
              {["daily", "weekly", "monthly"].map((g) => (
                <button
                  key={g}
                  onClick={() => setGranularity(g)}
                  className={`px-3 py-1 text-xs rounded ${
                    granularity === g
                      ? "bg-[#427BCA] text-white"
                      : isDarkMode
                        ? "text-gray-400"
                        : "text-gray-600"
                  }`}
                >
                  {g[0].toUpperCase() + g.slice(1)}
                </button>
              ))}
            </div>
            <button
              onClick={load}
              className={`p-2 rounded-lg ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </div>

        {/* Streak badges */}
        {primary?.streaks && (
          <div className="flex gap-3 flex-wrap">
            {primary.streaks.currentDailyStreak > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/20 text-orange-400 border border-orange-500/30">
                <Flame size={16} />
                <span className="text-sm font-semibold">
                  {primary.streaks.currentDailyStreak} day streak
                </span>
              </div>
            )}
            {primary.streaks.currentWeeklyStreak > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30">
                <Award size={16} />
                <span className="text-sm font-semibold">
                  {primary.streaks.currentWeeklyStreak} week streak
                </span>
              </div>
            )}
            {primary.streaks.longestDailyStreak > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">
                <TrendingUp size={16} />
                <span className="text-sm font-semibold">
                  Best: {primary.streaks.longestDailyStreak} days
                </span>
              </div>
            )}
          </div>
        )}

        {/* Primary KPIs */}
        {primary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <KpiCard
              title="Today"
              value={`${todayStat.actual} / ${todayStat.target}`}
              subtitle={`${todayStat.percentageAchieved}% of ${primary.label}`}
              icon={Target}
              colorCls="bg-blue-500/20 text-blue-400"
              isDarkMode={isDarkMode}
            />
            <KpiCard
              title="This Week"
              value={`${primary.thisWeek.actual} / ${primary.thisWeek.target}`}
              subtitle={`${pct(primary.thisWeek.actual, primary.thisWeek.target)}% of weekly target`}
              icon={Calendar}
              colorCls="bg-purple-500/20 text-purple-400"
              isDarkMode={isDarkMode}
            />
            <KpiCard
              title="This Month"
              value={`${primary.thisMonth.actual} / ${primary.thisMonth.target}`}
              subtitle={`${pct(primary.thisMonth.actual, primary.thisMonth.target)}% of monthly target`}
              icon={TrendingUp}
              colorCls="bg-green-500/20 text-green-400"
              isDarkMode={isDarkMode}
            />
            <KpiCard
              title="Consistency"
              value={`${primary.summary.consistencyRate}%`}
              subtitle={`${primary.summary.daysOnTarget}/${primary.summary.daysWithTarget} days hit`}
              icon={Award}
              colorCls="bg-orange-500/20 text-orange-400"
              isDarkMode={isDarkMode}
            />
          </div>
        )}

        {/* Monthly progress bar if primary has monthly target */}
        {primary?.thisMonth?.target > 0 && (
          <div className={`rounded-xl border p-6 ${cardBg}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">
                Monthly progress — {primary.label}
              </h3>
              <span className={`text-sm ${mutedText}`}>
                {primary.thisMonth.actual} / {primary.thisMonth.target}
              </span>
            </div>
            <div className="w-full h-3 rounded-full bg-gray-700/40 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  pct(primary.thisMonth.actual, primary.thisMonth.target) >= 100
                    ? "bg-green-400"
                    : "bg-[#427BCA]"
                }`}
                style={{
                  width: `${Math.min(100, pct(primary.thisMonth.actual, primary.thisMonth.target))}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Per-team, per-metric detail cards */}
        {data.teams?.map((team) => (
          <div key={team.teamId} className="space-y-3">
            <h2 className="text-base font-semibold">{team.teamName}</h2>
            {team.metrics.map((m) => (
              <MetricPanel
                key={m.metricId}
                metric={m}
                granularity={granularity}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Reusable sub-components ──
const KpiCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  colorCls,
  isDarkMode,
}) => {
  const cardBg = isDarkMode
    ? "bg-[#0C0E12] border-gray-700"
    : "bg-white border-gray-200";
  const muted = isDarkMode ? "text-[#94979C]" : "text-gray-500";
  return (
    <div className={`rounded-xl border p-4 ${cardBg}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-sm ${muted}`}>{title}</span>
        <div className={`p-2 rounded-lg ${colorCls}`}>
          <Icon size={18} />
        </div>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      <div className={`text-xs mt-1 ${muted}`}>{subtitle}</div>
    </div>
  );
};

const MetricPanel = ({ metric, granularity, isDarkMode }) => {
  const cardBg = isDarkMode
    ? "bg-[#0C0E12] border-gray-700"
    : "bg-white border-gray-200";
  const muted = isDarkMode ? "text-[#94979C]" : "text-gray-500";

  const chartData = (metric.dailyStats || []).slice(-14).map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    Target: d.target,
    Actual: d.actual,
  }));

  return (
    <div className={`rounded-xl border p-5 ${cardBg}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{metric.icon}</span>
          <div>
            <p className="font-semibold">{metric.label}</p>
            <p className={`text-xs ${muted}`}>
              {metric.summary.totalActual}/{metric.summary.totalTarget} past 4
              weeks · {metric.summary.overallPercentage}%
            </p>
          </div>
        </div>
        {metric.streaks?.currentDailyStreak > 0 && (
          <div className="flex items-center gap-1 text-orange-400 text-sm">
            <Flame size={14} />
            {metric.streaks.currentDailyStreak}
          </div>
        )}
      </div>

      <div className="w-full h-40">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={isDarkMode ? "#2a2e36" : "#e5e7eb"}
            />
            <XAxis
              dataKey="date"
              stroke={isDarkMode ? "#94979C" : "#6b7280"}
              tick={{ fontSize: 10 }}
            />
            <YAxis
              stroke={isDarkMode ? "#94979C" : "#6b7280"}
              tick={{ fontSize: 10 }}
            />
            <Tooltip
              contentStyle={{
                background: isDarkMode ? "#0C0E12" : "#fff",
                border: "1px solid #427BCA",
                borderRadius: 8,
              }}
            />
            <Legend />
            <Bar dataKey="Target" fill="#427BCA" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Actual" fill="#22c55e" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MyPerformance;
